import type { SearchCrumb } from "@/apps/admin/components/PageHeader/useRouteTrail";

/**
 * The depth that lives in the query (FB-48): Collectors and Runs keep the open item, run, editor
 * and page story in `?item=&run=&edit=` and the like, so the top trail, which sees routes only,
 * would stop at "Collectors" or "Runs". Each crumb links to the address with just the parameters
 * down to it. Names are the kind of thing (the trail does not load the collector).
 */

const crumbAt = (label: string, entries: [string, string][]): SearchCrumb => ({
  label,
  search: new URLSearchParams(entries).toString(),
});

/** Collectors: › New collector, or › Collector › Edit | Run › Task. */
export const collectorsSearchTrail = (
  search: URLSearchParams
): SearchCrumb[] => {
  if (search.get("new") === "1")
    return [crumbAt("New collector", [["new", "1"]])];
  const item = search.get("item");
  if (item === null || item === "") return [];
  const itemEntry: [string, string] = ["item", item];
  const crumbs = [crumbAt("Collector", [itemEntry])];
  if (search.get("edit") === "1") {
    crumbs.push(crumbAt("Edit", [itemEntry, ["edit", "1"]]));
    return crumbs;
  }
  const run = search.get("run");
  if (run === null || run === "") return crumbs;
  const runEntry: [string, string] = ["run", run];
  crumbs.push(crumbAt("Run", [itemEntry, runEntry]));
  const story = search.get("runStory");
  if (search.get("runPanel") === "log") {
    crumbs.push(crumbAt("Log", [itemEntry, runEntry, ["runPanel", "log"]]));
  } else if (story !== null && story !== "") {
    crumbs.push(
      crumbAt("Page story", [
        itemEntry,
        runEntry,
        ["runStep", search.get("runStep") ?? ""],
        ["runStory", story],
      ])
    );
  }
  return crumbs;
};

/** Runs: › Run › Page story | Log. */
export const runsSearchTrail = (search: URLSearchParams): SearchCrumb[] => {
  const run = search.get("run");
  if (run === null || run === "") return [];
  const runEntry: [string, string] = ["run", run];
  const crumbs = [crumbAt("Run", [runEntry])];
  const story = search.get("story");
  if (search.get("panel") === "log") {
    crumbs.push(crumbAt("Log", [runEntry, ["panel", "log"]]));
  } else if (story !== null && story !== "") {
    crumbs.push(
      crumbAt("Page story", [
        runEntry,
        ["step", search.get("step") ?? ""],
        ["story", story],
      ])
    );
  }
  return crumbs;
};
